
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Filter, Edit2, Trash2, Mail, Phone, MapPin, Briefcase, FileText, Shirt, CalendarClock, X, Save, User, Clock, HardDrive, ShieldCheck, Check, DollarSign, ChevronDown, Circle, Smartphone, ChevronLeft, ChevronRight, UserCircle, Fingerprint, HeartPulse, Home, Calendar, LayoutGrid, List } from 'lucide-react';
import { DEPARTMENTS, SHIFTS } from '../constants';
import { Employee, Role, EmployeeStatus, AttendanceRecord, Shift } from '../types';
import { useAppContext } from '../context/AppContext';
import { useLocation } from 'react-router-dom';
import { uyar, hata, onayla } from '../components/GlobalModal';

const emptyEmployee: Employee = {
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  departmentId: DEPARTMENTS[0]?.id || '',
  unitId: 'u1',
  role: Role.USER,
  status: EmployeeStatus.PERMANENT,
  position: '',
  hireDate: new Date().toISOString().split('T')[0],
  salary: 0,
  grossSalary: 0,
  netSalary: 0,
  shiftId: SHIFTS[0]?.id || 's1',
  documents: [],
  leaveBalance: 14,
  clothingSize: 'M',
  allowedDevices: [],
  deviceUserId: '',
  nationalId: '',
  birthDate: '',
  gender: 'E',
  bloodType: '',
  address: '',
  emergencyContactName: '',
  emergencyContactPhone: ''
};

interface EmployeeCardProps {
  employee: Employee;
  onEdit: (e: Employee) => void;
  onDelete: (id: string, name: string) => void;
  getDepartmentName: (id: string) => string;
  getShiftInfo: (id: string) => { name: string, time: string };
  attendanceStatus: { label: string, color: string, dot: string };
  authorizedDeviceCount: number;
}

const EmployeeCard: React.FC<EmployeeCardProps> = ({
  employee,
  onEdit,
  onDelete,
  getDepartmentName,
  getShiftInfo,
  attendanceStatus,
  authorizedDeviceCount
}) => {
  const { t } = useTranslation();
  const shift = getShiftInfo(employee.shiftId);
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group relative">
      <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button 
          onClick={() => onEdit(employee)}
          className="p-1.5 bg-white text-blue-600 rounded-full shadow border border-slate-100 hover:bg-blue-50"
          title={t('common.edit')}
        >
          <Edit2 size={14} />
        </button>
        <button 
          onClick={() => onDelete(employee.id, `${employee.firstName} ${employee.lastName}`)}
          className="p-1.5 bg-white text-red-600 rounded-full shadow border border-slate-100 hover:bg-red-50"
          title={t('common.delete')}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="p-5">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg border-2 border-white shadow-sm">
                {employee.firstName[0]}{employee.lastName[0]}
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${attendanceStatus.dot} shadow-sm animate-pulse`}></div>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-800 truncate">{employee.firstName} {employee.lastName}</h3>
              <div className="flex items-center gap-2">
                <p className="text-xs text-slate-500 truncate">{employee.position}</p>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-widest border ${attendanceStatus.color}`}>
                  {attendanceStatus.label}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-2 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <Briefcase size={14} className="text-slate-400" />
            <span className="truncate">{getDepartmentName(employee.departmentId)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-slate-400" />
            <span title={`${t('personnel.shift')}: ${shift.name}`} className="truncate font-semibold text-slate-700">{shift.time}</span>
          </div>
          <div className="flex items-center gap-2">
             <Fingerprint size={14} className="text-slate-400" />
             <span className="text-xs font-semibold text-slate-500">{t('personnel.terminalId')}: {employee.deviceUserId || t('personnel.notAssigned')}</span>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <CalendarClock size={14} className="text-slate-400" />
            <span className={`text-xs ${employee.leaveBalance < 0 ? 'text-red-600 font-semibold' : ''}`}>
              {t('personnel.leave')}: {employee.leaveBalance} {t('personnel.days')}
            </span>
          </div>
        </div>
        
        <div className="mt-4 flex gap-2">
          <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
            employee.status === EmployeeStatus.PERMANENT ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {employee.status}
          </span>
          <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
            employee.role === Role.ADMIN ? 'bg-red-100 text-red-700' : employee.role === Role.MANAGER ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {employee.role === Role.ADMIN ? t('personnel.admin') : employee.role === Role.MANAGER ? t('personnel.manager') : t('personnel.standard')}
          </span>
        </div>
      </div>
    </div>
  );
}

const Personnel = () => {
  const { t } = useTranslation();
  const { employees, addEmployee, updateEmployee, deleteEmployee, devices, attendanceLogs } = useAppContext();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'personal' | 'contact' | 'devices'>('general');
  const [formData, setFormData] = useState<Employee>(emptyEmployee);
  const [isEditing, setIsEditing] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Handle direct edit link from other pages
  useEffect(() => {
    const state = location.state as { editEmployeeId?: string };
    if (state?.editEmployeeId) {
      const emp = employees.find(e => e.id === state.editEmployeeId);
      if (emp) {
        handleEdit(emp);
      }
    }
  }, [location.state, employees]);

  // Reset page when filtering
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter]);

  // Status helper logic
  const getAttendanceStatus = (empId: string) => {
    const lastLog = attendanceLogs
      .filter(l => l.employeeId === empId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
    
    if (!lastLog) return { label: t('personnel.noData'), color: 'bg-slate-50 text-slate-400 border-slate-100', dot: 'bg-slate-300' };

    return lastLog.type === 'ENTRY'
      ? { label: t('personnel.atWork'), color: 'bg-green-50 text-green-600 border-green-100', dot: 'bg-green-500' }
      : { label: t('personnel.outside'), color: 'bg-amber-50 text-amber-600 border-amber-100', dot: 'bg-amber-500' };
  };

  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const searchScope = `${emp.firstName} ${emp.lastName} ${emp.position} ${emp.email} ${emp.deviceUserId}`.toLowerCase();
      const matchesSearch = searchScope.includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'ALL' || emp.role === roleFilter;
      const matchesStatus = statusFilter === 'ALL' || emp.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [employees, searchTerm, roleFilter, statusFilter]);

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredEmployees.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredEmployees, currentPage]);

  const getDepartmentName = (id: string) => DEPARTMENTS.find(d => d.id === id)?.name || t('personnel.unknown');
  
  const getShiftInfo = (id: string) => {
    const shift = SHIFTS.find(s => s.id === id);
    return {
      name: shift?.name || t('personnel.notAssigned'),
      time: shift ? `${shift.startTime} - ${shift.endTime}` : '--:--'
    };
  };

  const handleAdd = () => {
    setFormData({ ...emptyEmployee, id: `e${Date.now()}` }); 
    setIsEditing(false);
    setActiveTab('general');
    setShowModal(true);
  };

  const handleEdit = (employee: Employee) => {
    setFormData({ ...employee });
    setIsEditing(true);
    setActiveTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (await onayla(t('personnel.confirmDelete', { name }))) {
      deleteEmployee(id);
    }
  };

  const handleSave = () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      uyar(t('personnel.requiredFieldsAlert'));
      setActiveTab('general');
      return;
    }

    if (isEditing) {
      updateEmployee(formData.id, formData);
    } else {
      addEmployee(formData);
    }
    setShowModal(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'salary' || name === 'leaveBalance' || name === 'grossSalary' || name === 'netSalary') ? Number(value) : value
    }));
  };

  const toggleDeviceAssignment = (deviceId: string) => {
    setFormData(prev => {
      const isAssigned = prev.allowedDevices.includes(deviceId);
      const newAllowed = isAssigned 
        ? prev.allowedDevices.filter(id => id !== deviceId)
        : [...prev.allowedDevices, deviceId];
      return { ...prev, allowedDevices: newAllowed };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <h1 className="text-xl font-bold text-slate-800 tracking-tight">{t('personnel.title')}</h1>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder={t('personnel.searchPlaceholder')} 
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button 
              onClick={handleAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all shadow-md active:scale-95 whitespace-nowrap"
            >
              <Plus size={18} />
              <span>{t('personnel.newRecord')}</span>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-center bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500">
            <Filter size={16} />
            <span className="text-xs font-bold uppercase tracking-widest">{t('personnel.filters')}:</span>
          </div>
          
          <div className="flex gap-2">
            <select 
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">{t('personnel.allRoles')}</option>
              <option value={Role.ADMIN}>{t('personnel.admin')}</option>
              <option value={Role.MANAGER}>{t('personnel.manager')}</option>
              <option value={Role.USER}>{t('personnel.standardUser')}</option>
            </select>

            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">{t('personnel.allStatuses')}</option>
              {Object.values(EmployeeStatus).map(status => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              {t('personnel.showingOf', { shown: paginatedEmployees.length, total: filteredEmployees.length })}
            </span>
            <div className="flex bg-slate-50 border border-slate-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'card' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                title={t('personnel.cardView')}
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                title={t('personnel.listView')}
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'card' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {paginatedEmployees.map(emp => (
          <EmployeeCard
            key={emp.id}
            employee={emp} 
            onEdit={handleEdit}
            onDelete={handleDelete}
            getDepartmentName={getDepartmentName}
            getShiftInfo={getShiftInfo}
            attendanceStatus={getAttendanceStatus(emp.id)}
            authorizedDeviceCount={emp.allowedDevices.length}
          />
        ))}
      </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('personnel.employee')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('personnel.position')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('personnel.department')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('personnel.shift')}</th>
                <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('personnel.status')}</th>
                <th className="text-right px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('personnel.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {paginatedEmployees.map(emp => {
                const shift = getShiftInfo(emp.shiftId);
                const status = getAttendanceStatus(emp.id);
                return (
                  <tr key={emp.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-10 h-10 rounded-xl bg-blue-100 border-2 border-white shadow-sm flex items-center justify-center text-blue-600 font-bold text-sm">
                            {emp.firstName[0]}{emp.lastName[0]}
                          </div>
                          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${status.dot}`}></div>
                        </div>
                        <div>
                          <div className="font-bold text-slate-800">{emp.firstName} {emp.lastName}</div>
                          <div className="text-[10px] text-slate-400 font-medium">ID: {emp.deviceUserId || t('personnel.notAssigned')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-slate-700">{emp.position || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-slate-700">{getDepartmentName(emp.departmentId)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className="text-sm font-bold text-slate-700">{shift.time}</span>
                        <div className="text-[10px] text-slate-400">{shift.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(emp)} className="p-2 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title={t('common.edit')}>
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(emp.id, `${emp.firstName} ${emp.lastName}`)} className="p-2 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title={t('common.delete')}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-6">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-all"
          >
            <ChevronLeft size={18} />
          </button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }).map((_, idx) => {
              const pageNum = idx + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`min-w-[40px] h-10 px-3 rounded-lg text-sm font-bold transition-all ${
                    currentPage === pageNum 
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' 
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-all"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-slate-900/75 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[2.5rem] w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl border border-slate-100 flex flex-col animate-in zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight uppercase">
                  {isEditing ? t('personnel.updateRecord') : t('personnel.newRecord')}
                </h2>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">
                  {t('personnel.systemId')}: <span className="text-blue-600">{formData.id || t('personnel.automatic')}</span>
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-800 p-2 hover:bg-white rounded-full transition-all">
                <X size={28} />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 px-8 pt-4 bg-slate-50/30 border-b border-slate-100">
               {[
                 { id: 'general', label: t('personnel.tabWork'), icon: <Briefcase size={16}/> },
                 { id: 'personal', label: t('personnel.tabPersonal'), icon: <UserCircle size={16}/> },
                 { id: 'contact', label: t('personnel.tabContact'), icon: <Mail size={16}/> },
                 { id: 'devices', label: t('personnel.tabSystem'), icon: <ShieldCheck size={16}/> },
               ].map(tab => (
                 <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest border-b-2 transition-all ${
                      activeTab === tab.id 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                 >
                   {tab.icon}
                   {tab.label}
                 </button>
               ))}
            </div>
            
            <div className="flex-1 overflow-y-auto p-10 custom-scrollbar">
              {activeTab === 'general' && (
                <div className="space-y-10 animate-in fade-in duration-300">
                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('employees.firstName')} *</label>
                        <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none transition-all" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('employees.lastName')} *</label>
                        <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none transition-all" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.positionTitle')}</label>
                        <input type="text" name="position" value={formData.position} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none transition-all" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.department')}</label>
                        <select name="departmentId" value={formData.departmentId} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none bg-white">
                          {DEPARTMENTS.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.shiftAssignment')}</label>
                        <select name="shiftId" value={formData.shiftId} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none bg-white">
                          {SHIFTS.map(s => <option key={s.id} value={s.id}>{s.name} ({s.startTime}-{s.endTime})</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.employmentType')}</label>
                        <select name="status" value={formData.status} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none bg-white">
                          {Object.values(EmployeeStatus).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                   </div>

                   <div className="p-8 bg-blue-50/50 rounded-3xl border border-blue-100/50">
                      <h4 className="text-[11px] font-black text-blue-700 uppercase tracking-widest mb-6 flex items-center gap-2">
                        <DollarSign size={16}/> {t('personnel.financialInfo')}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.hireDate')}</label>
                          <div className="relative">
                            <input type="date" name="hireDate" value={formData.hireDate} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-white border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500" />
                            <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.grossSalary')}</label>
                          <div className="relative">
                            <input type="number" name="grossSalary" value={formData.grossSalary || ''} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-white border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500" placeholder="0" />
                            <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-green-600" size={18} />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.netSalary')}</label>
                          <div className="relative">
                            <input type="number" name="netSalary" value={formData.netSalary || ''} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-white border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500" placeholder="0" />
                            <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-600" size={18} />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.leaveBalance')}</label>
                          <div className="relative">
                            <input type="number" name="leaveBalance" value={formData.leaveBalance} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-white border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500" />
                            <CalendarClock className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-500" size={18} />
                          </div>
                        </div>
                      </div>
                   </div>
                </div>
              )}

              {activeTab === 'personal' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.nationalId')}</label>
                        <div className="relative">
                          <input type="text" maxLength={11} name="nationalId" value={formData.nationalId} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none transition-all" placeholder={t('personnel.nationalIdPlaceholder')} />
                          <Fingerprint className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.birthDate')}</label>
                        <input type="date" name="birthDate" value={formData.birthDate} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none transition-all" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.bloodType')}</label>
                        <div className="relative">
                          <select name="bloodType" value={formData.bloodType} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none bg-white">
                            <option value="">{t('personnel.selectOption')}</option>
                            {['A Rh+', 'A Rh-', 'B Rh+', 'B Rh-', 'AB Rh+', 'AB Rh-', '0 Rh+', '0 Rh-'].map(bt => <option key={bt} value={bt}>{bt}</option>)}
                          </select>
                          <HeartPulse className="absolute left-4 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.gender')}</label>
                        <div className="grid grid-cols-2 gap-3">
                           <button type="button" onClick={() => setFormData({...formData, gender: 'E'})} className={`py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${formData.gender === 'E' ? 'bg-blue-600 text-white border-blue-600 shadow-lg' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'}`}>{t('personnel.male')}</button>
                           <button type="button" onClick={() => setFormData({...formData, gender: 'K'})} className={`py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${formData.gender === 'K' ? 'bg-blue-600 text-white border-blue-600 shadow-lg' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'}`}>{t('personnel.female')}</button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.clothingSize')}</label>
                        <div className="relative">
                          <select name="clothingSize" value={formData.clothingSize} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none bg-white">
                            {['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'].map(size => <option key={size} value={size}>{size}</option>)}
                          </select>
                          <Shirt className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        </div>
                      </div>
                   </div>
                </div>
              )}

              {activeTab === 'contact' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('common.email')} *</label>
                        <div className="relative">
                          <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none transition-all" />
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('common.phone')}</label>
                        <div className="relative">
                          <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none transition-all" />
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.homeAddress')}</label>
                        <div className="relative">
                          <textarea name="address" rows={3} value={formData.address} onChange={handleInputChange} className="w-full px-10 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none transition-all resize-none" />
                          <Home className="absolute left-4 top-6 text-slate-400" size={18} />
                        </div>
                      </div>
                      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-slate-100">
                         <div>
                           <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.emergencyContact')}</label>
                           <input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10" placeholder={t('personnel.fullName')} />
                         </div>
                         <div>
                           <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.emergencyPhone')}</label>
                           <input type="text" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleInputChange} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10" placeholder="05XX XXX XX XX" />
                         </div>
                      </div>
                   </div>
                </div>
              )}

              {activeTab === 'devices' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                   <div className="bg-slate-900 text-white rounded-[2rem] p-8 shadow-xl">
                      <div className="flex items-center gap-4 mb-8">
                         <div className="p-3 bg-blue-600 rounded-2xl shadow-lg shadow-blue-500/20">
                            <Fingerprint size={24} />
                         </div>
                         <div>
                            <h4 className="text-lg font-black tracking-tight uppercase">{t('personnel.hardwareSecurity')}</h4>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{t('personnel.biometricData')}</p>
                         </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                         <div>
                            <label className="block text-[10px] font-black text-blue-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.terminalUserId')} *</label>
                            <input type="text" name="deviceUserId" value={formData.deviceUserId} onChange={handleInputChange} className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm font-black focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none transition-all text-white placeholder:text-slate-600" placeholder={t('personnel.terminalIdPlaceholder')} />
                            <p className="text-[10px] text-slate-500 mt-2 italic font-medium">{t('personnel.terminalIdNote')}</p>
                         </div>
                         <div>
                            <label className="block text-[10px] font-black text-blue-400 uppercase tracking-widest mb-2 ml-1">{t('personnel.systemRole')}</label>
                            <select name="role" value={formData.role} onChange={handleInputChange} className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl text-sm font-black focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none bg-slate-800 text-white">
                               <option value={Role.USER}>{t('personnel.standardUser')}</option>
                               <option value={Role.MANAGER}>{t('personnel.manager')}</option>
                               <option value={Role.ADMIN}>{t('personnel.sysAdmin')}</option>
                            </select>
                         </div>
                      </div>
                   </div>

                   <div className="space-y-4">
                      <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-[0.2em] border-b border-slate-100 pb-2 flex items-center gap-2">
                        <HardDrive size={14}/> {t('personnel.authorizedTerminals')}
                      </h4>
                      <p className="text-xs text-slate-500 font-medium italic">{t('personnel.selectTerminalsHint')}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {devices.map(device => {
                          const isAssigned = formData.allowedDevices.includes(device.id);
                          return (
                            <button
                              key={device.id}
                              type="button"
                              onClick={() => toggleDeviceAssignment(device.id)}
                              className={`p-4 rounded-2xl flex items-center justify-between border transition-all ${
                                isAssigned 
                                  ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/10' 
                                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                 <HardDrive size={16} className={isAssigned ? 'text-white' : 'text-slate-400'} />
                                 <div className="text-left">
                                    <div className="text-[10px] font-black uppercase tracking-widest leading-none">{device.name}</div>
                                    <div className={`text-[9px] mt-1 ${isAssigned ? 'text-blue-100' : 'text-slate-400'}`}>{device.ipAddress}</div>
                                 </div>
                              </div>
                              {isAssigned && <Check size={18} />}
                            </button>
                          );
                        })}
                      </div>
                   </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-8 border-t border-slate-100 flex justify-end gap-4 bg-slate-50/50">
              <button 
                onClick={() => setShowModal(false)} 
                className="px-8 py-4 text-xs font-black uppercase tracking-widest text-slate-500 hover:bg-slate-200 rounded-2xl transition-all"
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleSave}
                className="px-12 py-4 bg-slate-900 text-white rounded-2xl hover:bg-blue-600 transition-all shadow-xl shadow-slate-200 font-black text-xs uppercase tracking-widest flex items-center gap-3 active:scale-95"
              >
                <Save size={18} />
                {isEditing ? t('personnel.updateInfo') : t('personnel.completeRecord')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Personnel;
